import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SlidersHorizontal, Grid3X3, List, Map as MapIcon, Image, Disc } from "lucide-react";
import { ListingTypeSection } from "./ListingTypeSection";
import { ManufacturerDropdown } from "./ManufacturerDropdown";
import DatabaseDrivenFilters from "../DatabaseDrivenFilters";
import { MarketplaceSearchInput } from "./MarketplaceSearchInput";
import { SortDropdown } from "./SortDropdown";
import { cn } from "@/lib/utils";
import { JOB_CATEGORY_OPTIONS } from "@/types/jobWizard";

interface FilterHierarchyProps {
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  sellListingType: string | null;
  buyRequestType: string | null;
  onSellTypeChange: (type: string | null) => void;
  onBuyTypeChange: (type: string | null) => void;
  partsListingType?: string | null;
  onPartsTypeChange?: (type: string | null) => void;
  manufacturers: string[];
  selectedManufacturers: string[];
  manufacturerCounts: Record<string, number>;
  onManufacturersChange: (manufacturers: string[]) => void;
  filters: any;
  onFiltersChange: (filters: any) => void;
  sellCounts?: {
    sale?: number;
    wet_lease?: number;
    dry_lease?: number;
  };
  buyCounts?: {
    purchase?: number;
    wet_lease?: number;
    dry_lease?: number;
  };
  showAdvancedFilters: boolean;
  onToggleAdvancedFilters: () => void;
  activeFiltersCount: number;
  viewMode: 'grid' | 'list' | 'map';
  onViewModeChange: (mode: 'grid' | 'list' | 'map') => void;
  markerDisplayMode?: 'icon' | 'hero';
  onMarkerDisplayModeChange?: (mode: 'icon' | 'hero') => void;
  marketMode?: 'all' | 'sell' | 'buy';
  onMarketModeChange?: (mode: 'all' | 'sell' | 'buy') => void;
}

export const FilterHierarchy = ({
  assetType,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  sellListingType,
  buyRequestType,
  onSellTypeChange,
  onBuyTypeChange,
  partsListingType,
  onPartsTypeChange,
  manufacturers,
  selectedManufacturers,
  manufacturerCounts,
  onManufacturersChange,
  filters,
  onFiltersChange,
  sellCounts = {},
  buyCounts = {},
  showAdvancedFilters,
  onToggleAdvancedFilters,
  activeFiltersCount,
  viewMode,
  onViewModeChange,
  markerDisplayMode = 'icon',
  onMarkerDisplayModeChange,
  marketMode = 'all',
  onMarketModeChange
}: FilterHierarchyProps) => {
  
  const sellOptions = assetType === 'services'
    ? [
        { id: 'service_offer', label: 'Service Providers', value: 'service_offer', count: sellCounts.sale },
        { id: 'capability_listing', label: 'Capabilities', value: 'capability_listing', count: sellCounts.wet_lease },
      ]
    : assetType === 'jobs'
    ? [
        { id: 'job_post', label: 'Job Postings', value: 'job_post', count: sellCounts.sale },
        { id: 'job_contract', label: 'Contract Work', value: 'job_contract', count: sellCounts.wet_lease },
      ]
    : assetType === 'parts'
    ? [
        { id: 'parts_sell_aircraft', label: 'For Sale (Aircraft)', value: 'parts_sell_aircraft', count: sellCounts.sale },
        { id: 'parts_sell_engine', label: 'For Sale (Engine)', value: 'parts_sell_engine', count: 0 },
        { id: 'parts_sell_general', label: 'For Sale (General)', value: 'parts_sell_general', count: 0 },
        { id: 'parts_rent', label: 'For Rent', value: 'parts_rent', count: sellCounts.wet_lease },
      ]
    : assetType === 'helicopter' 
    ? [
        { id: 'sale', label: 'For Sale', value: 'sale', count: sellCounts.sale },
        { id: 'wet_lease', label: 'Wet Lease', value: 'wet_lease', count: sellCounts.wet_lease },
        { id: 'dry_lease', label: 'Dry Lease', value: 'dry_lease', count: sellCounts.dry_lease },
      ]
    : [
        { id: 'engine_sell', label: 'For Sale', value: 'engine_sell', count: sellCounts.sale },
        { id: 'engine_rent', label: 'For Rent', value: 'engine_rent', count: sellCounts.wet_lease },
      ];

  const buyOptions = assetType === 'services'
    ? [
        { id: 'service_request', label: 'Service Requests', value: 'service_request', count: buyCounts.purchase },
        { id: 'rfq_request', label: 'RFQ', value: 'rfq_request', count: buyCounts.wet_lease },
      ]
    : assetType === 'jobs'
    ? [
        { id: 'job_profile', label: 'Professionals', value: 'job_profile', count: buyCounts.purchase },
        { id: 'job_seeking', label: 'Job Seekers', value: 'job_seeking', count: buyCounts.wet_lease },
      ]
    : assetType === 'parts'
    ? [
        { id: 'parts_buy_aircraft', label: 'Buy Request (Aircraft)', value: 'parts_buy_aircraft', count: buyCounts.purchase },
        { id: 'parts_buy_engine', label: 'Buy Request (Engine)', value: 'parts_buy_engine', count: 0 },
        { id: 'parts_buy_general', label: 'Buy Request (General)', value: 'parts_buy_general', count: 0 },
        { id: 'parts_rent_request', label: 'Rent Request', value: 'parts_rent_request', count: buyCounts.wet_lease },
      ]
    : assetType === 'helicopter'
    ? [
        { id: 'purchase', label: 'Purchase', value: 'buy_sale', count: buyCounts.purchase },
        { id: 'wet_lease', label: 'Wet Lease', value: 'buy_wet_lease', count: buyCounts.wet_lease },
        { id: 'dry_lease', label: 'Dry Lease', value: 'buy_dry_lease', count: buyCounts.dry_lease },
      ]
    : [
        { id: 'engine_buy', label: 'Buy Request', value: 'engine_buy', count: buyCounts.purchase },
        { id: 'engine_rent_request', label: 'Rent Request', value: 'engine_rent_request', count: buyCounts.wet_lease },
      ];

  const handleSellTypeSelect = (type: string | null) => {
    onSellTypeChange(type);
    if (type !== null) {
      onBuyTypeChange(null);
      onMarketModeChange?.('sell');
    } else {
      onMarketModeChange?.('all');
    }
  };

  const handleBuyTypeSelect = (type: string | null) => {
    onBuyTypeChange(type);
    if (type !== null) {
      onSellTypeChange(null);
      onMarketModeChange?.('buy');
    } else {
      onMarketModeChange?.('all');
    }
  };

  const handlePartsTypeSelect = (type: string | null) => {
    if (onPartsTypeChange) {
      onPartsTypeChange(type);
      if (type === null) {
        onMarketModeChange?.('all');
      }
    }
  };

  const handleSellLabelClick = () => {
    if (marketMode === 'sell') {
      onMarketModeChange?.('all');
      onSellTypeChange(null);
    } else {
      onMarketModeChange?.('sell');
      onSellTypeChange(null);
      onBuyTypeChange(null);
    }
  };

  const handleBuyLabelClick = () => {
    if (marketMode === 'buy') {
      onMarketModeChange?.('all');
      onBuyTypeChange(null);
    } else {
      onMarketModeChange?.('buy');
      onBuyTypeChange(null);
      onSellTypeChange(null);
    }
  };

  const ViewModeButton = ({ mode, icon: Icon }: { mode: 'grid' | 'list' | 'map', icon: any }) => (
    <button
      onClick={() => onViewModeChange(mode)}
      aria-label={`${mode} view`}
      className={cn(
        "h-8 w-8 flex items-center justify-center transition-all duration-200 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        viewMode === mode 
          ? "bg-primary text-primary-foreground" 
          : "text-muted-foreground hover:text-foreground hover:bg-card"
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="space-y-3">
      {/* Row 1: Search, Sort, View Mode, Filters - Unified Control Bar */}
      <div className={cn(
        "flex items-center gap-3 p-3 rounded-xl flex-wrap",
        "bg-white/80 dark:bg-slate-900/80",
        "backdrop-blur-sm",
        "border border-slate-200/60 dark:border-slate-700/40",
        "shadow-sm"
      )}>
        <div data-tutorial-id="marketplace-search" className="flex-1 min-w-[200px]">
          <MarketplaceSearchInput
            assetType={assetType}
            value={searchQuery}
            onChange={onSearchChange}
            className="w-full"
          />
        </div>
        <div data-tutorial-id="marketplace-sort">
          <SortDropdown
            assetType={assetType}
            value={sortBy}
            onChange={onSortChange}
          />
        </div>
        
        {/* View Mode Toggle - Enhanced pill style */}
        <div className={cn(
          "flex p-1 rounded-xl",
          "bg-slate-100 dark:bg-slate-800",
          "border border-slate-200/60 dark:border-slate-700/40"
        )}>
          <ViewModeButton mode="grid" icon={Grid3X3} />
          <ViewModeButton mode="list" icon={List} />
          <ViewModeButton mode="map" icon={MapIcon} />
        </div>
        
        {/* Icon/Hero toggle - only visible in map view */}
        {viewMode === 'map' && onMarkerDisplayModeChange && (
          <button
            onClick={() => onMarkerDisplayModeChange(markerDisplayMode === 'icon' ? 'hero' : 'icon')}
            title={markerDisplayMode === 'icon' ? 'Show Photos' : 'Show Icons'}
            aria-label={markerDisplayMode === 'icon' ? 'Show photo markers' : 'Show icon markers'}
            className={cn(
              "h-9 w-9 flex items-center justify-center transition-all duration-300 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "border border-slate-200/60 dark:border-slate-700/40",
              markerDisplayMode === 'hero'
                ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                : "bg-white dark:bg-slate-800 text-muted-foreground hover:text-foreground hover:border-primary/40"
            )}
          >
            {markerDisplayMode === 'icon' ? (
              <Image className="h-4 w-4" />
            ) : (
              <Disc className="h-4 w-4" />
            )}
          </button>
        )}
        
        {/* Filters Button - Premium Badge Style */}
        <button
          onClick={onToggleAdvancedFilters}
          data-tutorial-id="marketplace-filters"
          className={cn(
            "h-9 px-4 rounded-xl text-xs font-semibold transition-all duration-300 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            showAdvancedFilters 
              ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" 
              : cn(
                "bg-white dark:bg-slate-800",
                "text-foreground",
                "border border-slate-200 dark:border-slate-700",
                "hover:border-primary/40 hover:shadow-md"
              )
          )}
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filters</span>
          {activeFiltersCount > 0 && (
            <span className={cn(
              "h-5 min-w-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center",
              "bg-secondary text-secondary-foreground"
            )}>
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Row 2: Listing Type Filters - Enhanced Glass Panel */}
      <div className={cn(
        "flex flex-wrap items-center gap-x-8 gap-y-3 py-3 px-4 rounded-xl",
        "bg-gradient-to-r from-slate-50 via-white to-slate-50",
        "dark:from-slate-900/50 dark:via-slate-800/50 dark:to-slate-900/50",
        "border border-slate-200/50 dark:border-slate-700/30"
      )}>
        {assetType === 'parts' ? (
          <>
            <ListingTypeSection
              title="Buy"
              options={[
                { id: 'parts_buy_aircraft', value: 'parts_buy_aircraft', label: 'Aircraft', count: 0 },
                { id: 'parts_buy_engine', value: 'parts_buy_engine', label: 'Engine', count: 0 },
                { id: 'parts_buy_general', value: 'parts_buy_general', label: 'General', count: 0 },
                { id: 'parts_rent_request', value: 'parts_rent_request', label: 'Rent', count: 0 },
              ]}
              selectedValue={partsListingType || null}
              onSelect={handlePartsTypeSelect}
              variant="buy"
              onLabelClick={handleBuyLabelClick}
              isLabelActive={marketMode === 'buy'}
            />
            {/* Visual separator between Buy and Sell */}
            <div className="hidden sm:block w-px h-8 bg-gradient-to-b from-transparent via-slate-300 dark:via-slate-600 to-transparent" />
            <ListingTypeSection
              title="Sell"
              options={[
                { id: 'parts_sell_aircraft', value: 'parts_sell_aircraft', label: 'Aircraft', count: 0 },
                { id: 'parts_sell_engine', value: 'parts_sell_engine', label: 'Engine', count: 0 },
                { id: 'parts_sell_general', value: 'parts_sell_general', label: 'General', count: 0 },
                { id: 'parts_rent', value: 'parts_rent', label: 'Rent Out', count: 0 },
              ]}
              selectedValue={partsListingType || null}
              onSelect={handlePartsTypeSelect}
              variant="sell"
              onLabelClick={handleSellLabelClick}
              isLabelActive={marketMode === 'sell'}
            />
          </>
        ) : (
          <>
            <ListingTypeSection
              title="Buy"
              options={buyOptions}
              selectedValue={buyRequestType}
              onSelect={handleBuyTypeSelect}
              variant="buy"
              assetType={assetType}
              onLabelClick={handleBuyLabelClick}
              isLabelActive={marketMode === 'buy'}
            />
            {/* Visual separator between Buy and Sell */}
            <div className="hidden sm:block w-px h-8 bg-gradient-to-b from-transparent via-slate-300 dark:via-slate-600 to-transparent" />
            <ListingTypeSection
              title="Sell"
              options={sellOptions}
              selectedValue={sellListingType}
              onSelect={handleSellTypeSelect}
              variant="sell"
              assetType={assetType}
              onLabelClick={handleSellLabelClick}
              isLabelActive={marketMode === 'sell'}
            />
          </>
        )}

        {/* Manufacturer inline - hide for jobs and services */}
        {assetType !== 'jobs' && assetType !== 'services' && (
          <>
            {/* Visual separator before Brand */}
            <div className="hidden sm:block w-px h-8 bg-gradient-to-b from-transparent via-slate-300 dark:via-slate-600 to-transparent" />
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 rounded-full bg-gradient-to-b from-primary/40 to-primary" />
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">
                Brand
              </span>
              <ManufacturerDropdown
                manufacturers={manufacturers}
                selected={selectedManufacturers}
                counts={manufacturerCounts}
                onChange={onManufacturersChange}
              />
            </div>
          </>
        )}
      </div>

      {/* Row 3: Category Pills for Services & Jobs - Enhanced Visual Badges */}
      {(assetType === 'services' || assetType === 'jobs') && (
        <div className={cn(
          "flex flex-wrap items-center gap-2 py-3 px-4 rounded-xl",
          "bg-gradient-to-r from-slate-50 via-white to-slate-50",
          "dark:from-slate-900/50 dark:via-slate-800/50 dark:to-slate-900/50",
          "border border-slate-200/50 dark:border-slate-700/30"
        )}>
          <div className="flex items-center gap-2 mr-2">
            <div className="w-1 h-5 rounded-full bg-gradient-to-b from-primary/40 to-primary" />
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">
              {assetType === 'services' ? 'Category' : 'Role'}
            </span>
          </div>
          
          {assetType === 'services' && (
            <>
              {[
                { id: 'mro', label: 'MRO', color: 'violet' },
                { id: 'camo', label: 'CAMO', color: 'blue' },
                { id: 'part21', label: 'Part-21', color: 'orange' },
                { id: 'training', label: 'Training', color: 'green' },
                { id: 'operator', label: 'Operator', color: 'sky' },
                { id: 'engineering', label: 'Engineering', color: 'purple' },
                { id: 'ground_ops', label: 'Ground Ops', color: 'amber' },
                { id: 'fbo_heliport', label: 'FBO', color: 'emerald' },
                { id: 'management', label: 'Management', color: 'slate' },
                { id: 'uas', label: 'UAS', color: 'cyan' },
              ].map((cat) => {
                const isSelected = (filters.serviceCategories || []).includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      const current = filters.serviceCategories || [];
                      const updated = current.includes(cat.id) 
                        ? current.filter((c: string) => c !== cat.id) 
                        : [...current, cat.id];
                      onFiltersChange({ ...filters, serviceCategories: updated });
                    }}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-200 border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20 scale-105"
                        : cn(
                          "bg-white dark:bg-slate-800",
                          "text-slate-600 dark:text-slate-400",
                          "border-slate-200 dark:border-slate-700",
                          "hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-700 hover:shadow-sm"
                        )
                    )}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </>
          )}
          {assetType === 'jobs' && (
            <>
              {JOB_CATEGORY_OPTIONS.map((cat) => {
                const isSelected = (filters.jobCategories || []).includes(cat.value);
                return (
                  <button
                    key={cat.value}
                    onClick={() => {
                      const current = filters.jobCategories || [];
                      const updated = current.includes(cat.value) 
                        ? current.filter((c: string) => c !== cat.value) 
                        : [...current, cat.value];
                      onFiltersChange({ ...filters, jobCategories: updated });
                    }}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-200 border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20 scale-105"
                        : cn(
                          "bg-white dark:bg-slate-800",
                          "text-slate-600 dark:text-slate-400",
                          "border-slate-200 dark:border-slate-700",
                          "hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-700 hover:shadow-sm"
                        )
                    )}
                  >
                    {cat.shortLabel}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="pt-2 animate-fade-in">
          <ScrollArea className="max-h-[65vh] md:max-h-[60vh] min-h-[200px]">
            <DatabaseDrivenFilters
              assetType={assetType}
              filters={filters}
              onFiltersChange={onFiltersChange}
            />
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
